// Modules
// Search setup for global and category-specific course display
import {liteClient as algoliasearch} from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';

import searchBox from 'instantsearch.js/es/widgets/search-box/search-box.js';
import connectHits from 'instantsearch.js/es/connectors/hits/connectHits.js';
import refinementList from 'instantsearch.js/es/widgets/refinement-list/refinement-list.js';
import configure from 'instantsearch.js/es/widgets/configure/configure.js';
import hierarchicalMenu from 'instantsearch.js/es/widgets/hierarchical-menu/hierarchical-menu.js';
import connectCurrentRefinements from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements.js';

const indexPrefix = global.env === 'live' ? 'live' : 'staging';

// Initialize client
const searchClient = algoliasearch('IPCDBSIGLM', '1e39ee67b9882027388311bfe299734d');

// Utility to get currency symbol from cookie
const getCurrencySymbol = (currency) => {
	const value = currency?.toLowerCase() ?? 'eur';
	return {
		usd: '$',
		gbp: '£',
		sar: 'SAR'
	}[value] ?? '€';
};

function bindRefinementVisibility(instance) {
	instance.on('render', () => {
		document.querySelectorAll('.ais-RefinementList').forEach(list => {
			const parentGroup = list.closest('.sidebar-filters__group');
			if (!parentGroup) return;
			
			if (list.classList.contains('ais-RefinementList--noRefinement')) {
				parentGroup.style.display = 'none';
			} else {
				parentGroup.style.display = '';
			}
		});
	});
}

function addLangPrefixToUrl(rawUrl) {
	if (!rawUrl) return rawUrl;
	
	let url;
	try {
		url = new URL(rawUrl, location.origin);
	} catch (e) {
		return rawUrl;
	}
	
	const lang = (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') ? Weglot.getCurrentLang() : null;
	const original = global.lang_original || null;
	
	if (!lang || (original && lang === original)) {
		return rawUrl;
	}
	
	const rawSegments = url.pathname.split('/').filter(Boolean);
	
	if (rawSegments[0] === lang) return url.toString();
	
	let insertPos = 0;
	if (global.site_base) {
		const baseSegs = global.site_base.split('/').filter(Boolean);
		if (baseSegs.length && rawSegments.slice(0, baseSegs.length).join('/') === baseSegs.join('/')) {
			insertPos = baseSegs.length;
		}
	}
	
	rawSegments.splice(insertPos, 0, lang);
	url.pathname = '/' + rawSegments.join('/') + (url.pathname.endsWith('/') ? '/' : '');
	
	return url.toString();
}

// Utility to recursively find category logo
const findLogoRecursive = (category) => {
	if (!category) return '';
	if (category.logo) return category.logo;
	if (Array.isArray(category.parents)) {
		for (let i = category.parents.length - 1; i >= 0; i--) {
			const parent = WPJS.categories?.find(c => c.slug === category.parents[i].slug);
			if (parent?.logo) return parent.logo;
		}
	}
	return '';
};

// Utility to build checkbox HTML
const buildCheckbox = (label, isRefined, idPrefix, translate) => {
	const id = `${idPrefix}${label.trim()}`;
	const textHtml = translate ? `<p class="wg-algolia">${label.trim()}</p>` : label.trim();
	
	return `
    <div class="checkbox">
      <input type="checkbox" ${isRefined ? 'checked' : ''} class="checkbox__input" id="${id}" />
      <label for="${id}" class="checkbox__label" role="button">
        <span class="checkbox__text">${textHtml}</span>
      </label>
    </div>
  `;
};

// Build refinement widgets (safe to call multiple times)
const refinementWidgets = (instance, opts = {}) => {
	const {
		skipCategories = false,
		categoriesSelector = '#algolia-refinement-categories',
		locationsSelector = '#algolia-refinement-locations',
		courseTypeSelector = '#algolia-refinement-course-type',
		tagsSelector = '#algolia-refinement-tags'
	} = opts;
	
	const commonClasses = {
		list: 'sidebar-filters__block',
		label: 'checkbox__label',
		checkbox: 'checkbox__input',
		labelText: 'checkbox__text',
		count: 'hidden',
		showMore: 'button button--outline button--rounded'
	};
	
	const addOnce = (selector, widgetFactory) => {
		const el = document.querySelector(selector);
		if (!el || el.dataset.widgetAttached === '1') return;
		instance.addWidgets([widgetFactory()]);
		el.dataset.widgetAttached = '1';
	};
	
	if (!skipCategories) {
		addOnce(categoriesSelector, () => hierarchicalMenu({
			container: categoriesSelector,
			attributes: ['taxonomies_hierarchical.courses_category.lvl0', 'taxonomies_hierarchical.courses_category.lvl1', 'taxonomies_hierarchical.courses_category.lvl2'],
			showParentLevel: true,
			limit: 100,
			showMore: false,
			showMoreLimit: 500,
			cssClasses: {
				...commonClasses,
				subList: 'sidebar-filters__sub',
				item: 'sidebar-filters__item',
				selectedItem: 'is-active',
				link: 'sidebar-filters__link'
			},
			templates: {
				item: ({
					label,
					isRefined
				}) => `<a class="checkbox__label ${isRefined ? 'is-active' : ''}">
               <span class="checkbox__text">${label}</span>
             </a>`
			}
		}));
	}
	
	addOnce(locationsSelector, () => hierarchicalMenu({
		container: locationsSelector,
		attributes: ['taxonomies_hierarchical.all_locations.lvl0', 'taxonomies_hierarchical.all_locations.lvl1'],
		showParentLevel: true,
		limit: 100,
		showMore: false,
		showMoreLimit: 500,
		cssClasses: {
			...commonClasses,
			subList: 'sidebar-filters__sub',
			item: 'sidebar-filters__item',
			selectedItem: 'is-active',
			link: 'sidebar-filters__link'
		},
		templates: {
			item: ({
				label,
				isRefined
			}) => {
				const country = String(label).split(' > ')[0].trim();
				const flag = (window.WPJS?.flags?.[country] || '').toUpperCase();
				const icon = flag ? `<svg width="16" height="16"><use xlink:href="${WPJS.themeUrl}dist/img/icons/flags/${flag}.svg"></use></svg>` : '';
				return `<a class="checkbox__label ${isRefined ? 'is-active' : ''}">
                    <span class="checkbox__text">${icon}${label}</span>
                  </a>`;
			}
		}
	}));
	
	addOnce(courseTypeSelector, () => refinementList({
		container: courseTypeSelector,
		attribute: 'taxonomies.course_type',
		limit: 50,
		showMore: false,
		showMoreLimit: 100,
		templates: {
			item: ({
				label,
				isRefined
			}) => buildCheckbox(label, isRefined, 'ctype_', true)
		},
		cssClasses: commonClasses
	}));
	
	addOnce(tagsSelector, () => refinementList({
		container: tagsSelector,
		attribute: 'course_tags',
		limit: 50,
		showMore: false,
		showMoreLimit: 100,
		templates: {
			item: ({
				label,
				isRefined
			}) => buildCheckbox(label, isRefined, 'tag_', true)
		},
		cssClasses: commonClasses
	}));
};

// Custom renderer for current refinements
const renderCurrentRefinements = ({
	items,
	refine,
	widgetParams
}) => {
	const container = widgetParams.container;
	if (!container) return;
	
	container.innerHTML = '';
	if (!items.length) return;
	
	items.forEach(item => {
		item.refinements.forEach(refinement => {
			const button = document.createElement('button');
			button.className = 'filter-chip';
			button.textContent = item.attribute === 'query' ? `Search: ${refinement.label}` : refinement.label;
			
			const remove = document.createElement('span');
			remove.className = 'filter-remove';
			remove.innerHTML = `
        <svg width="12" height="12">
          <use xlink:href="${global.template_url}/dist/img/icons/remove-2.svg"></use>
        </svg>`;
			
			button.appendChild(remove);
			button.addEventListener('click', e => {
				e.preventDefault();
				item.refine(refinement);
			});
			
			container.appendChild(button);
		});
	});
};

const customCurrentRefinements = connectCurrentRefinements(renderCurrentRefinements);

// Custom hits renderer (grouped by category or location)
const renderHits = ({
	hits,
	widgetParams,
	results = {}
}) => {
	
	const getDeepestCats = (hit) => {
		const hier = hit?.taxonomies_hierarchical?.courses_category || {};
		if (Array.isArray(hier.lvl2) && hier.lvl2.length) return hier.lvl2;
		if (Array.isArray(hier.lvl1) && hier.lvl1.length) return hier.lvl1;
		if (Array.isArray(hier.lvl0) && hier.lvl0.length) return hier.lvl0;
		const flat = hit?.taxonomies?.courses_category;
		return Array.isArray(flat) && flat.length ? flat : [];
	};
	
	const container = widgetParams.container;
	const isChildCategory = container.id === 'algolia-cat-child-results';
	const currentCat = container.dataset.cat;
	const isFinalLevel = container.dataset.catChild === '2';
	
	const label = document.querySelector('.top-row-results__label span');
	if (label) label.textContent = results.nbHits ?? hits.length;
	
	const grouped = {};
	
	if (isChildCategory && currentCat) {
		if (isFinalLevel) {
			grouped[currentCat] = hits;
		} else {
			hits.forEach(hit => {
				
				const parentLevel = Math.max(0, parseInt(container.dataset.catChild, 10) - 1);
				const childKey = `lvl${parentLevel + 1}`;
				
				let childCategories = hit?.taxonomies_hierarchical?.courses_category?.[childKey] ?? [];
				
				if (!Array.isArray(childCategories) || !childCategories.length) {
					
					const parentCats = hit?.taxonomies_hierarchical?.courses_category?.[`lvl${parentLevel}`] ?? [];
					const matchesParent = Array.isArray(parentCats) && parentCats.includes(currentCat);
					if (matchesParent) {
						(grouped[currentCat] ??= []).push(hit);
						return;
					}
					
					(grouped['Uncategorized'] ??= []).push(hit);
					return;
				}
				
				childCategories.forEach(child => {
					(grouped[child] ??= []).push(hit);
				});
			});
		}
	} else {
		hits.forEach(hit => {
			let categories = getDeepestCats(hit);
			
			if (!Array.isArray(categories) || !categories.length) {
				categories = ['Uncategorized'];
			}
			
			categories.forEach(cat => {
				if (!grouped[cat]) {
					grouped[cat] = [];
				}
				if (!grouped[cat].includes(hit)) {
					grouped[cat].push(hit);
				}
			});
		});
	}
	
	const groupedEntries = Object.entries(grouped).map(([cat, groupHits]) => {
		const firstHitIndex = hits.findIndex(h => groupHits.includes(h));
		return {
			cat,
			groupHits,
			score: firstHitIndex >= 0 ? firstHitIndex : 99999
		};
	});
	
	groupedEntries.sort((a, b) => a.score - b.score);
	
	container.innerHTML = groupedEntries.map(({
		cat,
		groupHits,
		score
	}) => {
		const catParts = cat.split(' > ');
		const catLabel = catParts[catParts.length - 1].trim();
		const matchedCategory = (WPJS.categories || []).find(c => c.name === catLabel);
		
		const catLogo = matchedCategory?.logo || findLogoRecursive(matchedCategory) || '';
		const catLink = matchedCategory?.permalink ? addLangPrefixToUrl(matchedCategory.permalink) : '#';
		const catText = matchedCategory?.text || '';
		const hasCourses = catLabel.toLowerCase().endsWith('courses');
		const catLabelClean = hasCourses ? catLabel : `${catLabel} courses`;
		
		return `
      <div class="group-search-results" data-priority="Priority score: ${score}">
        <div class="group-search-results__info">
          <div class="group-search-results__col">
            <h3 class="group-search-results__title">
              ${isFinalLevel ? catLabel : `<a target="_blank" href="${catLink}">${catLabel}</a>`}
              ${!isFinalLevel ? `<span><a target="_blank" href="${catLink}"><div class="wg-algolia">View all</div> ${catLabelClean}</a></span>` : ''}
              ${catLogo ? `<img src="${catLogo}" alt="${catLabel} logo" loading="lazy">` : ''}
            </h3>
            <div class="group-search-results__descr wg-algolia">${catText}</div>
          </div>
        </div>
        <div class="group-search-results__items">
          ${groupHits.map(hit => renderHitItem(hit)).join('')}
        </div>
      </div>
    `;
	}).join('');
	
};

const renderHitItem = (hit) => {
	const locIds = Array.isArray(hit.locations_ids) ? hit.locations_ids.join(',') : '';
	const emptyClass = (!locIds || !hit.course_id) ? 'button--inactive' : '';
	const isElearning = hit.elearning;
	
	const rawPrice = parseFloat(hit.lowest_price?.eur ?? 0);
	const displayPrice = (rawPrice === 0) ? 'On request' : rawPrice.toFixed(2);
	const currency = getCurrencySymbol(document.cookie.match(/(?:^|; )currency=([^;]*)/)?.[1]);
	
	return `
    <div class="training-info-row" ${locIds ? `data-locations="${locIds}"` : ''} ${hit.course_id ? `data-course-id="${hit.course_id}"` : ''}>
      <div class="training-info-row__inner">
        <div class="training-info-row__about">
          <a href="${addLangPrefixToUrl(hit.permalink)}" class="training-info-row__title no-translate">${hit.post_title}</a>
          <div class="training-info-row__info">
            <div class="training-info-row__item-info">
              <svg width="16" height="16"><use xlink:href="${global.template_url}/dist/img/icons/icons.svg#star-yellow"></use></svg>
              <span>${hit.review_score || '4.9'}</span>
            </div>
            <div class="training-info-row__item-info">
              ${displayPrice === 'On request' ? `<span class="course-price wg-algolia">On request</span>` : `<span class="currency-symbol">${currency}</span>
                   <span class="course-price"
                     data-eur="${hit.lowest_price?.eur ?? ''}"
                     data-usd="${hit.lowest_price?.usd ?? ''}"
                     data-gbp="${hit.lowest_price?.gbp ?? ''}"
                     data-sar="${hit.lowest_price?.sar ?? ''}">
                     <div class="wg-algolia">from</div> <span class="price-number">${displayPrice}</span>
                   </span>`}
            </div>
            <div class="training-info-row__item-info">
              ${isElearning ? `<svg width="12" height="12"><use xlink:href="${global.template_url}/dist/img/icons/globe.svg"></use></svg><span>Online</span>` : `<svg width="16" height="16"><use xlink:href="${global.template_url}/dist/img/icons/icons.svg#pin"></use></svg>
                   <span class="wg-algolia">${hit.prices_and_locations?.length || 0} ${hit.prices_and_locations?.length === 1 ? 'Location' : 'Locations'}</span>`}
            </div>
          </div>
        </div>
        <div class="training-info-row__actions">
          <a href="${addLangPrefixToUrl(hit.permalink)}" class="training-info-row__button button button--rounded button--outline"><div class="wg-algolia">Course details</div></a>
          ${isElearning ? `<a href="#" data-post-id="${hit.post_id}" data-popup=".item-cart-added" class="training-info-row__button button button--rounded button--green e-add-to-cart ${emptyClass}"><div class="wg-algolia">Add to Cart</div></a>` : `<a href="#" data-popup=".single-course-modal" class="training-info-row__button button button--rounded button--green course-bar-btn ${emptyClass}"><div class="wg-algolia">Check availability</div></a>`}
        </div>
      </div>
    </div>
  `;
};

// Category-specific search instance
const catContainer = document.querySelector('#algolia-cat-child-results');
const wrapper = document.querySelector('.wrapper');

const getCoordsFromWrapper = () => {
	if (!wrapper || !wrapper.dataset.geo) return null;
	const [lat, lng] = wrapper.dataset.geo.split(',').map(parseFloat);
	return (!isNaN(lat) && !isNaN(lng)) ? {
		latitude: lat,
		longitude: lng
	} : null;
};

if (catContainer && catContainer.dataset.cat && catContainer.dataset.catFull) {
	const categoryFull = catContainer.dataset.catFull.trim();
	const catDepth = parseInt(catContainer.dataset.catChild, 10) || 1;
	const filterAttr = `taxonomies_hierarchical.courses_category.lvl${catDepth - 1}`;
	const childKeyComputed = `lvl${catDepth}`;
	const catSearch = instantsearch({
		indexName: `${indexPrefix}_posts_courses`,
		searchClient,
		routing: false
	});
	
	const buildWidgets = (coords) => {
		const around = coords ? `${coords.latitude},${coords.longitude}` : null;
		
		const cfg = configure({
			hitsPerPage: 9999,
			distinct: false,
			filters: `${filterAttr}:"${categoryFull.replace(/"/g, '\\"')}"`,
			facets: [`taxonomies_hierarchical.courses_category.lvl${catDepth - 1}`, `taxonomies_hierarchical.courses_category.lvl${catDepth}`],
			maxValuesPerFacet: 500, ...(around ? {aroundLatLng: around} : {})
		});
		
		const widgets = [cfg, connectHits((params) => renderHits({
			...params,
			widgetParams: {
				...params.widgetParams,
				childKey: childKeyComputed
			}
		}))({container: catContainer}),];
		
		const boxEl = document.querySelector('.search-form__category');
		if (boxEl) {
			widgets.push(searchBox({
				container: '.search-form__category',
				placeholder: 'Search by course name...',
				showReset: false,
				showSubmit: false,
				showLoadingIndicator: true,
				cssClasses: {input: 'input'}
			}));
		}
		
		refinementWidgets(catSearch);
		
		return widgets;
	};
	
	let started = false;
	const start = (coords = null) => {
		if (started) return;
		catSearch.addWidgets(buildWidgets(coords));
		catSearch.start();
		started = true;
	};
	
	const fallbackCatCoords = getCoordsFromWrapper();
	start(fallbackCatCoords);
	
	if (navigator.geolocation) {
		navigator.geolocation.getCurrentPosition(({coords}) => {
			catSearch.addWidgets([configure({aroundLatLng: `${coords.latitude},${coords.longitude}`})]);
			catSearch.refresh();
		});
	}
	
	bindRefinementVisibility(catSearch);
	
} else {
	
	const search = instantsearch({
		indexName: `${indexPrefix}_posts_courses`,
		searchClient,
		routing: true
	});
	
	if (document.querySelector('.search-form__archive')) {
		search.addWidgets([searchBox({
			container: '.search-form__archive',
			placeholder: 'Search by course name...',
			showReset: false,
			showSubmit: false,
			showLoadingIndicator: true,
			cssClasses: {input: 'input wg-algolia'}
		})]);
	}
	
	search.addWidgets([customCurrentRefinements({
		container: document.querySelector('.current-filters'),
		includedAttributes: ['query', 'taxonomies.course_type', 'course_tags', 'all_locations_names', 'taxonomies_hierarchical.courses_category.lvl0', 'taxonomies_hierarchical.courses_category.lvl1', 'taxonomies_hierarchical.courses_category.lvl2']
	})]);
	
	refinementWidgets(search);
	
	if (document.querySelector('#algolia-course-results')) {
		const searchInput = document.querySelector('#search-results');
		const searchQuery = new URLSearchParams(window.location.search).get('s') || '';
		
		if (searchInput) searchInput.value = searchQuery;
		
		search.addWidgets([configure({
			hitsPerPage: 9999,
			query: searchQuery
		}), connectHits(renderHits)({container: document.querySelector('#algolia-course-results')})]);
		
		if (searchInput) {
			searchInput.addEventListener('input', e => {
				search.helper.setQuery(e.target.value).search();
			});
		}
	}
	
	const fallbackCoords = getCoordsFromWrapper();
	
	search.addWidgets([configure({
		hitsPerPage: 9999, ...(fallbackCoords ? {aroundLatLng: `${fallbackCoords.latitude},${fallbackCoords.longitude}`} : {})
	})]);
	search.start();
	
	if (navigator.geolocation) {
		navigator.geolocation.getCurrentPosition(({coords}) => {
			search.addWidgets([configure({aroundLatLng: `${coords.latitude},${coords.longitude}`})]);
			search.refresh();
		});
	}
	
	bindRefinementVisibility(search);
}